import { PERMISSION_STORAGE_KEY, PERMISSION_MODULES, SRM_MODULES, FULL_ACCESS_PERMISSIONS } from "../constants/permissions";

const parsePermissionValue = (value) => {
  if (typeof value !== "string") return value;

  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

const getPermissionName = (permission) => {
  if (typeof permission === "string" || typeof permission === "number") {
    return String(permission);
  }

  if (!permission || typeof permission !== "object") return "";

  return String(
    permission.feature_name ??
      permission.permission_name ??
      permission.name ??
      "",
  );
};

const normalizePermissionList = (permissions) =>
  Array.isArray(permissions)
    ? permissions.map(getPermissionName).filter(Boolean)
    : [];

const findModulePermissions = (value, moduleName, visited = new Set()) => {
  if (typeof moduleName !== "string" || !moduleName.trim()) return [];
  const parsedValue = parsePermissionValue(value);
  if (!parsedValue || typeof parsedValue !== "object") return [];
  if (visited.has(parsedValue)) return [];
  visited.add(parsedValue);

  if (Array.isArray(parsedValue)) {
    for (const item of parsedValue) {
      const modulePermissions = findModulePermissions(item, moduleName, visited);
      if (modulePermissions.length > 0) return modulePermissions;
    }
    return [];
  }

  const currentModuleName =
    parsedValue.module_name ?? parsedValue.moduleName ?? parsedValue.name;
  if (
    typeof currentModuleName === "string" &&
    currentModuleName.trim().toLowerCase() === moduleName.trim().toLowerCase()
  ) {
    const featureList =
      parsedValue.features ??
      parsedValue.permissions ??
      parsedValue.items ??
      parsedValue.actions;
    return normalizePermissionList(featureList);
  }

  const matchingKey = Object.keys(parsedValue).find(
    (key) => key.trim().toLowerCase() === moduleName.trim().toLowerCase(),
  );

  if (matchingKey && Array.isArray(parsedValue[matchingKey])) {
    return normalizePermissionList(parsedValue[matchingKey]);
  }

  for (const nestedValue of Object.values(parsedValue)) {
    const modulePermissions = findModulePermissions(
      nestedValue,
      moduleName,
      visited,
    );
    if (modulePermissions.length > 0) return modulePermissions;
  }

  return [];
};

export const extractPermissions = (loginData) => {
  return Object.values(PERMISSION_MODULES).reduce((permissions, moduleName) => {
    const modulePermissions = findModulePermissions(loginData, moduleName);
    if (modulePermissions.length > 0) {
      permissions[moduleName] = modulePermissions;
    }
    return permissions;
  }, {});
};

export const getStoredPermissions = () => {
  try {
    return parsePermissionValue(
      localStorage.getItem(PERMISSION_STORAGE_KEY),
    ) || {};
  } catch {
    return {};
  }
};

export const hasPermission = (moduleName, permissionName) => {
  if (
    typeof moduleName !== "string" ||
    !moduleName.trim() ||
    typeof permissionName !== "string" ||
    !permissionName.trim()
  ) {
    return false;
  }
  const permissions = getStoredPermissions();
  const modulePermissions = findModulePermissions(permissions, moduleName);
  return modulePermissions.some(
    (permission) =>
      String(permission).trim().toLowerCase() ===
      permissionName.trim().toLowerCase(),
  );
};

export const hasAnyModulePermission = (moduleName) => {
  const permissions = getStoredPermissions();
  return findModulePermissions(permissions, moduleName).length > 0;
};

export const hasFullAccess = (moduleName) =>
  hasPermission(moduleName, FULL_ACCESS_PERMISSIONS.FULL_ACCESS);

export const hasAnySrmPermission = () =>
  SRM_MODULES.some((moduleName) => hasAnyModulePermission(moduleName));

export const hasAnyPermission = (moduleName, permissionNames) =>
  Array.isArray(permissionNames) && permissionNames.some((permissionName) =>
    hasPermission(moduleName, permissionName),
  );
